import {Component, OnDestroy, OnInit} from '@angular/core';
import {V3ExamSession, V3ExamSessionPart, V3ExerciseSet} from "../../../cspa/model/v3/cspa";
import {ActivatedRoute} from "@angular/router";
import {forkJoin, map, Observable, of, switchMap, tap} from "rxjs";
import {NativeServiceApiProvider} from "../../../services/native-api-provider.service";
import {AuthorizationServiceProvider} from "../../../auth_profile/services/authorization-service.provider";
import {SpinnerService} from "../../../utils/services/spinner.service";
import {V3Student} from "../../../auth_profile/model/v3/schools";

@Component({
    selector: 'app-teacher-student-exam-results',
    templateUrl: './teacher-student-exam-results.component.html',
    styleUrls: ['./teacher-student-exam-results.component.scss'],
    standalone: false
})
export class TeacherStudentExamResultsComponent implements OnInit, OnDestroy {

  private schoolId: number
  student: V3Student;
  exams: V3ExamSession[]
  exerciseSets: Map<string, V3ExerciseSet> = null;
  exerciseSetExams: Map<V3ExerciseSet, V3ExamSession[]> = new Map<V3ExerciseSet, V3ExamSession[]>();
  examPartsBySessionUuid: Map<string, V3ExamSessionPart[]> = new Map<string, V3ExamSessionPart[]>();
  exerciseSetEntries: [V3ExerciseSet, V3ExamSession[]][] = []
  private readonly emptyParts: V3ExamSessionPart[] = []

  constructor(private route: ActivatedRoute,
              private provider: NativeServiceApiProvider,
              private authService: AuthorizationServiceProvider,
              private spinnerService: SpinnerService,
  ) {
  }

  ngOnInit(): void {
    this.loadSchoolId().pipe(
      switchMap(_ => this.loadStudent()),
      switchMap(_ => this.loadExerciseSets()),
      switchMap(_ => this.loadExamSessions()),
    ).subscribe()
  }

  ngOnDestroy(): void {
  }

  private loadSchoolId() {
    return this.authService.getAuthDetailsService().pipe(
      switchMap(api => api.getSelfSchoolId()),
      tap(schoolId => this.schoolId = schoolId)
    )
  }

  private loadStudent() {
    return this.route.parent.paramMap.pipe(
      switchMap(params =>
        this.getStudent(this.schoolId, +params.get('studentId'))
      ),
      tap<V3Student>(student => this.student = student)
    )
  }

  private getStudent(schoolId: number, studentId: number) {
    return this.provider.casa().pipe(
      switchMap(api => api.getStudent(schoolId, studentId))
    )
  }

  loadExerciseSets(): Observable<any> {
    if (this.exerciseSets && this.exerciseSets.size > 0) return of(this.exerciseSets)
    return this.provider.cspaV3().pipe(
      switchMap(api => api.listExerciseSets()),
      map(sets =>
        sets.reduce((res, current) => res.set(current.path, current)
          , new Map<string, V3ExerciseSet>())),
      tap(setsMap => this.exerciseSets = setsMap)
    )
  }

  private loadExamSessions() {
    return this.spinnerService.trace(
      this.provider.cspaV3().pipe(
        switchMap(api => api.findExamSessions(this.student.person.id, "")),
        switchMap(exams => this.loadExamParts(exams).pipe(
          map(partsByUuid => ({ exams, partsByUuid }))
        )),
        tap(({ exams, partsByUuid }) => {
          this.exams = exams.sort((a, b) => a.path.localeCompare(b.path))
          this.examPartsBySessionUuid = partsByUuid
          this.setExerciseSetExams()
        }),
      )
    )
  }

  private loadExamParts(exams: V3ExamSession[]): Observable<Map<string, V3ExamSessionPart[]>> {
    if (!exams || exams.length === 0) {
      return of(new Map<string, V3ExamSessionPart[]>())
    }
    return this.provider.cspaV3().pipe(
      switchMap(api =>
        forkJoin(
          exams.map(exam =>
            api.findExamSessionsParts(this.student.person.id, exam.uuid).pipe(
              map(parts => [exam.uuid, parts] as [string, V3ExamSessionPart[]])
            )
          )
        )
      ),
      map(entries => new Map<string, V3ExamSessionPart[]>(entries))
    )
  }

  getExamParts(exam: V3ExamSession) {
    return this.examPartsBySessionUuid.get(exam.uuid) || this.emptyParts
  }

  setExerciseSetExams() {
    this.exerciseSetExams.clear()
    for (let set of this.exerciseSets.values()) {
      this.exerciseSetExams.set(
        set,
        this.exams.filter(e => e.path.split("_")[0] == set.path)
          .sort((a, b) =>
            a.path.split("_")[3].localeCompare(b.path.split("_")[3])
          )
          .sort((a, b) =>
            +a.path.split("_")[1] - +b.path.split("_")[1]
          )
      )
    }
    this.exerciseSetEntries = Array.from(this.exerciseSetExams.entries())
      .filter(([_, exams]) => exams.length > 0)
  }

  getExamScore(exam: V3ExamSession) {
    const parts = this.examPartsBySessionUuid.get(exam.uuid) || []
    return parts.reduce((sum, p) => sum + (p.score * p.weight), 0)
  }

  trackByExerciseSet(_: number, entry: [V3ExerciseSet, V3ExamSession[]]): string {
    return entry[0].path
  }

  trackByExamSession(_: number, exam: V3ExamSession): string {
    return exam.uuid
  }

  trackByExamPart(_: number, examPart: V3ExamSessionPart): string {
    return `${examPart.path}:${examPart.num}`
  }
}
